import { Dialog, DialogContent, DialogTitle, DialogClose } from '@/components/Dialog';
import R3ImageArea from '@/pages/Home/Generator/components/R3ImageArea';
import { cn } from '@/utils';
import { useCallback, useMemo } from 'react';
import { create } from 'zustand';
import { useDimensions } from '@/hooks/useDimensions';
import { DialogDescription } from '@radix-ui/react-dialog';
import { NotchedBackground } from '../components/NotchedBackground';
import { X } from 'lucide-react';
import { SessionType } from '@/hooks/useSessionStore';
import JenErrorToast from '@/components/ui/jen-error-toast';
import { toast } from 'sonner';

interface R3ImageDialogStore {
  open: boolean;
  disabled: boolean;
  setOpen: (open: boolean) => void;
  setDisabled: (disabled: boolean) => void;
}

const useR3ImageDialogStore = create<R3ImageDialogStore>((set) => ({
  open: false,
  disabled: false,
  setOpen: (open) => set({ open }),
  setDisabled: (disabled) => set({ disabled }),
}));

export const useR3ImageDialog = (session: SessionType) => {
  const { open, disabled, setOpen, setDisabled } = useR3ImageDialogStore();

  const { isMobileOrPad, isIPhoneSEorSmaller } = useDimensions();

  const show = useCallback(() => {
    if (session.user.plan.key === 'base') {
      return toast(
        <JenErrorToast
          error={new Error(`${session.user.plan.name} does not have access to upload. Please upgrade to use this feature.`)}
        />,
        {
          className: '!border-0 !shadow-none',
          unstyled: true,
        },
      );
    }
    setOpen(true);
  }, [setOpen]);

  const hide = useCallback(() => {
    setOpen(false);
  }, [setOpen]);

  const R3ImageDialogComponent = useMemo(
    () =>
      function R3ImageDialog() {
        return (
          <Dialog
            open={open}
            onOpenChange={(isOpen) => {
              if (!isOpen) {
                hide();
              } else {
                setOpen(true);
              }
            }}
          >
            <DialogContent
              closePosition="right"
              className={cn('p-0  bg-transparent border-[18px] overflow-hidden', isMobileOrPad && 'sm:p-2')}
              style={{
                borderRadius: 'none',
                border: 0,
                padding: 0,
              }}
              onPointerDownOutside={(e) => {
                e.preventDefault();
                hide();
              }}
              onEscapeKeyDown={(e) => {
                e.preventDefault();
                hide();
              }}
            >
              <DialogClose asChild onClick={hide}>
                <button className="absolute right-4 top-4 rounded-sm opacity-70 bg-white/20 p-1 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground">
                  <X className="h-4 w-4" />
                  <span className="sr-only">Close</span>
                </button>
              </DialogClose>
              <NotchedBackground height="480px">
                <div className={cn('absolute inset-0 z-10 w-full p-6')}>
                  <R3ImageArea disabled={disabled} hide={hide} />
                </div>
              </NotchedBackground>
            </DialogContent>
            <DialogDescription />
          </Dialog>
        );
      },
    [open, disabled, isIPhoneSEorSmaller],
  );

  return {
    open,
    disabled,
    show,
    hide,
    setDisabled,
    R3ImageDialogComponent,
  };
};
